import React, { useState, useEffect } from "react";
import Nav from "./Navigation";
import { getTokenInternal } from "./useToken";

const FriendList = () => {
  const [allFriends, setAllFriends] = useState([]);
  const [selectedFriendId, setSelectedFriendId] = useState(null);
  const [friendItems, setFriendItems] = useState([]);
  const [itemVisibility, setItemVisibility] = useState({});
  const [selectedEvent, setSelectedEvent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalImageSrc, setModalImageSrc] = useState(null);

  const fetchFriends = async () => {
    try {
      const token = await getTokenInternal();

      if (token) {
        const url = `${process.env.REACT_APP_API_HOST}/api/friendsList`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.ok) {
          const data = await response.json();
          setAllFriends(data);
        }
      }
    } catch (error) {
      console.error("Error fetching friends:", error);
    }
  };

  useEffect(() => {
    fetchFriends();
  }, []);

  const handleFriendSelect = async (friendId) => {
    try {
      const token = await getTokenInternal();
      if (token) {
        const url = `${process.env.REACT_APP_API_HOST}/users/${friendId}/items`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const friendItems = await response.json();
          const filteredList = friendItems.filter(
            (item) => item.games_id === 1 && !item.checked
          );
          const initialVisibility = filteredList.reduce((acc, item) => {
            acc[item.name] = false;
            return acc;
          }, {});
          setItemVisibility(initialVisibility);
          setFriendItems(filteredList);
        } else {
          console.error("Error fetching friend's items:", response.statusText);
        }
      }
    } catch (error) {
      console.error("Error fetching friend's items:", error.message);
    }
  };

  const getCurrentUserId = async () => {
    try {
      const token = await getTokenInternal();
      return token ? token.account.id : null;
    } catch (error) {
      console.error("Error getting current user ID:", error);
      return null;
    }
  };

  const handleEventSelection = (e) => {
    const selectedValue = e.target.value;
    setSelectedEvent(selectedValue);
  };

  const handleClearSelection = () => {
    setSelectedEvent("");
  };

  const toggleVisibility = (itemName) => {
    setItemVisibility((prevVisibility) => ({
      ...prevVisibility,
      [itemName]: !prevVisibility[itemName],
    }));
  };

  const showModal = (imageSrc) => {
    setModalImageSrc(imageSrc);
  };

  const hideModal = () => {
    setModalImageSrc(null);
  };

  const filterItemsBySearchQuery = (items) =>
    items.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const nonEventItems = friendItems.filter((item) => item.event === "Primary");
  const otherEvents = friendItems.filter((item) => item.event !== "Primary");
  const selectedEvents = friendItems.filter(
    (item) => item.event === selectedEvent
  );

  return (
    <div className="text-white">
      <Nav />
      <div className="max-w-xl px-4 py-8">
        <h2 className="text-2xl font-bold mb-4">What Friend Needs What</h2>
        <div className="relative">
          <select
            className="text-black font-bold block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:border-indigo-500 focus:ring focus:ring-indigo-200"
            onChange={async (e) => {
              const friendId = e.target.value;
              const selectedFriend = allFriends.find(
                (friend) => friend.id === parseInt(friendId)
              );
              const currentUserId = await getCurrentUserId();

              if (selectedFriend) {
                const selectedId =
                  selectedFriend.user_id !== currentUserId
                    ? selectedFriend.user_id
                    : selectedFriend.friend_id;

                setSelectedFriendId(selectedId);
                handleFriendSelect(selectedId);
              }
            }}
          >
            <option value="">Select a friend</option>
            {allFriends && allFriends.length > 0 ? (
              allFriends.map((friend) => (
                <option key={friend.id} value={friend.id}>
                  {friend.friend_username}
                </option>
              ))
            ) : (
              <option value="">No friends found</option>
            )}
          </select>
        </div>

        <div className="mb-4 mt-4">
          <label htmlFor="eventDropdown" className="text-md font-bold">
            Select Event:
          </label>
          <select
            id="eventDropdown"
            onChange={handleEventSelection}
            value={selectedEvent}
            className="ml-2 p-1 border rounded text-black"
          >
            <option value="">None</option>
            {Array.from(new Set(otherEvents.map((item) => item.event))).map(
              (event) => (
                <option key={event} value={event}>
                  {event}
                </option>
              )
            )}
          </select>
          <button
            onClick={handleClearSelection}
            className="ml-2 p-1 border rounded"
          >
            Clear
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="p-1 border rounded text-black w-full"
          />
        </div>

        {selectedFriendId && (
          <div className="mt-4">
            <h3 className="text-xl font-bold mb-2">Friend's Needed Items:</h3>
            <p className="font-bold text-xl underline">{selectedEvent}</p>
            <div className="flex">
              {Array.from(
                new Set(
                  filterItemsBySearchQuery(selectedEvents).map(
                    (item) => item.types
                  )
                )
              ).map((type) => (
                <div key={type} className="mb-4 mb-6 ml-2">
                  <h4 className="text-md font-bold mb-2">{type}</h4>
                  <div className="grid gap-2">
                    {filterItemsBySearchQuery(selectedEvents)
                      .filter((item) => item.types === type)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((filteredItem) => (
                        <div
                          className={`border p-1 rounded bg-neutral-400 flex items-center`}
                          key={filteredItem.user_item_id}
                        >
                          <div className="flex">
                            <div className="ml-2">{filteredItem.name}</div>
                          </div>
                          <button
                            className="ml-2 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                            onClick={() =>
                              toggleVisibility(filteredItem.name)
                            }
                          >
                            {itemVisibility[filteredItem.name] ? "Hide" : "Info"}
                          </button>
                          {itemVisibility[filteredItem.name] && (
                            <div className="mt-2">
                              <div className="flex">
                                <div> Map: </div>
                                <div className="ml-2">
                                  {filteredItem.maps_name}
                                </div>
                              </div>
                              <img
                                    className="object-scale-down h-48 cursor-pointer"
                                    src={`/imagesitems/${filteredItem.url}`}
                                    alt="Description of the image"
                                    onClick={() => showModal(`/imagesitems/${filteredItem.url}`)}
                                    onError={(e) => {
                                      e.target.src = '/gamelogos/Unofficial.PNG'; // Set fallback image on error
                                    }}
                                  />
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex">
              <div className="mb-4 mb-6 ml-2">
                <h4 className="text-md font-bold mb-2">Primary</h4>
                <div className="flex">
              {Array.from(
                new Set(
                  filterItemsBySearchQuery(nonEventItems).map(
                    (item) => item.types
                  )
                )
              ).map((type) => (
                <div key={type} className="mb-4 mb-6 ml-2">
                  <h4 className="text-md font-bold mb-2">{type}</h4>
                  <div className="grid gap-2">
                    {filterItemsBySearchQuery(nonEventItems)
                      .filter((item) => item.types === type)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((filteredItem) => (
                        <div
                          className={`border p-1 rounded bg-neutral-400 flex items-center`}
                          key={filteredItem.user_item_id}
                        >
                          <div className="flex">
                            <div className="ml-2">{filteredItem.name}</div>
                          </div>
                          <button
                            className="ml-2 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                            onClick={() =>
                              toggleVisibility(filteredItem.name)
                            }
                          >
                            {itemVisibility[filteredItem.name] ? "Hide" : "Info"}
                          </button>
                          {itemVisibility[filteredItem.name] && (
                            <div className="mt-2">
                              <div className="flex">
                                <div> Map: </div>
                                <div className="ml-2">
                                  {filteredItem.maps_name}
                                </div>
                              </div>
                                <img
                                    className="object-scale-down h-48 cursor-pointer"
                                    src={`/imagesitems/${filteredItem.url}`}
                                    alt="Description of the image"
                                    onClick={() => showModal(`/imagesitems/${filteredItem.url}`)}
                                    onError={(e) => {
                                      e.target.src = '/gamelogos/Unofficial.PNG'; // Set fallback image on error
                                    }}
                                />
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal for image */}
      {modalImageSrc && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50" onClick={hideModal}>
          <img
            src={modalImageSrc}
            alt="Modal Content"
            className="max-w-full max-h-full"
          />
        </div>
      )}
    </div>
  );
};

export default FriendList;

// import React, { useState, useEffect } from "react";
// import Nav from "./Navigation";
// import { getTokenInternal } from "./useToken";

// const FriendList = () => {
//   const [allFriends, setAllFriends] = useState([]);
//   const [selectedFriendId, setSelectedFriendId] = useState(null);
//   const [friendItems, setFriendItems] = useState([]);
//   const [itemVisibility, setItemVisibility] = useState({});
//   const [selectedEvent, setSelectedEvent] = useState("");
//   const [searchQuery, setSearchQuery] = useState("");
//   const [modalImageSrc, setModalImageSrc] = useState(null);

//   const fetchFriends = async () => {
//     try {
//       const token = await getTokenInternal();

//       if (token) {
//         const url = `${process.env.REACT_APP_API_HOST}/api/friendsList`;
//         const response = await fetch(url, {
//           method: "GET",
//           headers: {
//             Authorization: `Bearer ${token.access_token}`,
//             "Content-Type": "application/json",
//           },
//         });
//         if (response.ok) {
//           const data = await response.json();
//           setAllFriends(data);
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching friends:", error);
//     }
//   };

//   useEffect(() => {
//     fetchFriends();
//   }, []);

//   const handleFriendSelect = async (friendId) => {
//     try {
//       const token = await getTokenInternal();
//       if (token) {
//         const url = `${process.env.REACT_APP_API_HOST}/users/${friendId}/items`;
//         const response = await fetch(url, {
//           method: "GET",
//           headers: {
//             Authorization: `Bearer ${token.access_token}`,
//             "Content-Type": "application/json",
//           },
//         });

//         if (response.ok) {
//           const friendItems = await response.json();
//           const filteredList = friendItems.filter(
//             (item) => item.games_id === 1 && !item.checked
//           );
//           const initialVisibility = filteredList.reduce((acc, item) => {
//             acc[item.name] = false;
//             return acc;
//           }, {});
//           setItemVisibility(initialVisibility);
//           setFriendItems(filteredList);
//         } else {
//           console.error("Error fetching friend's items:", response.statusText);
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching friend's items:", error.message);
//     }
//   };

//   const getCurrentUserId = async () => {
//     try {
//       const token = await getTokenInternal();
//       return token ? token.account.id : null;
//     } catch (error) {
//       console.error("Error getting current user ID:", error);
//       return null;
//     }
//   };

//   const handleEventSelection = (e) => {
//     const selectedValue = e.target.value;
//     setSelectedEvent(selectedValue);
//   };

//   const handleClearSelection = () => {
//     setSelectedEvent("");
//   };

//   const toggleVisibility = (itemName) => {
//     setItemVisibility((prevVisibility) => ({
//       ...prevVisibility,
//       [itemName]: !prevVisibility[itemName],
//     }));
//   };

//   const showModal = (src) => {
//     setModalImageSrc(src);
//   };

//   const hideModal = () => {
//     setModalImageSrc(null);
//   };

//   const filterItemsBySearchQuery = (items) =>
//     items.filter((item) =>
//       item.name.toLowerCase().includes(searchQuery.toLowerCase())
//     );

//   const nonEventItems = friendItems.filter((item) => item.event === "Primary");
//   const otherEvents = friendItems.filter((item) => item.event !== "Primary");
//   const selectedEvents = friendItems.filter(
//     (item) => item.event === selectedEvent
//   );

//   return (
//     <div className="text-white">
//       <Nav />
//       <div className="max-w-xl px-4 py-8">
//         <h2 className="text-2xl font-bold mb-4">What Friend Needs What</h2>
//         <div className="relative">
//           <select
//             className="text-black font-bold block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:border-indigo-500 focus:ring focus:ring-indigo-200"
//             onChange={async (e) => {
//               const friendId = e.target.value;
//               const selectedFriend = allFriends.find(
//                 (friend) => friend.id === parseInt(friendId)
//               );
//               const currentUserId = await getCurrentUserId();

//               if (selectedFriend) {
//                 const selectedId =
//                   selectedFriend.user_id !== currentUserId
//                     ? selectedFriend.user_id
//                     : selectedFriend.friend_id;

//                 setSelectedFriendId(selectedId);
//                 handleFriendSelect(selectedId);
//               }
//             }}
//           >
//             <option value="">Select a friend</option>
//             {allFriends && allFriends.length > 0 ? (
//               allFriends.map((friend) => (
//                 <option key={friend.id} value={friend.id}>
//                   {friend.friend_username}
//                 </option>
//               ))
//             ) : (
//               <option value="">No friends found</option>
//             )}
//           </select>
//         </div>

//         <div className="mb-4 mt-4">
//           <label htmlFor="eventDropdown" className="text-md font-bold">
//             Select Event:
//           </label>
//           <select
//             id="eventDropdown"
//             onChange={handleEventSelection}
//             value={selectedEvent}
//             className="ml-2 p-1 border rounded text-black"
//           >
//             <option value="">None</option>
//             {Array.from(new Set(otherEvents.map((item) => item.event))).map(
//               (event) => (
//                 <option key={event} value={event}>
//                   {event}
//                 </option>
//               )
//             )}
//           </select>
//           <button
//             onClick={handleClearSelection}
//             className="ml-2 p-1 border rounded"
//           >
//             Clear
//           </button>
//         </div>

//         <div className="mb-4">
//           <input
//             type="text"
//             placeholder="Search items..."
//             value={searchQuery}
//             onChange={(e) => setSearchQuery(e.target.value)}
//             className="p-1 border rounded text-black w-full"
//           />
//         </div>

//         {selectedFriendId && (
//           <div className="mt-4">
//             <h3 className="text-xl font-bold mb-2">Friend's Needed Items:</h3>
//             <p className="font-bold text-xl underline">{selectedEvent}</p>
//             <div className="flex">
//               {Array.from(
//                 new Set(
//                   filterItemsBySearchQuery(selectedEvents).map(
//                     (item) => item.types
//                   )
//                 )
//               ).map((type) => (
//                 <div key={type} className="mb-4 mb-6 ml-2">
//                   <h4 className="text-md font-bold mb-2">{type}</h4>
//                   <div className="grid gap-2">
//                     {filterItemsBySearchQuery(selectedEvents)
//                       .filter((item) => item.types === type)
//                       .sort((a, b) => a.name.localeCompare(b.name))
//                       .map((filteredItem) => (
//                         <div
//                           className={`border p-1 rounded bg-neutral-400 flex items-center`}
//                           key={filteredItem.user_item_id}
//                         >
//                           <div className="flex">
//                             <div className="ml-2">{filteredItem.name}</div>
//                           </div>
//                           <button
//                             className="ml-2 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
//                             onClick={() =>
//                               toggleVisibility(filteredItem.name)
//                             }
//                           >
//                             {itemVisibility[filteredItem.name] ? "Hide" : "Info"}
//                           </button>
//                           {itemVisibility[filteredItem.name] && (
//                             <div className="mt-2">
//                               <div className="flex">
//                                 <div> Map: </div>
//                                 <div className="ml-2">
//                                   {filteredItem.maps_name}
//                                 </div>
//                               </div>
//                               <img
//                                 className="object-scale-down h-48 cursor-pointer"
//                                 src={require(`../imagesitems/${filteredItem.url}`)}
//                                 alt="no image"
//                                 onClick={() =>
//                                   showModal(
//                                     require(`../imagesitems/${filteredItem.url}`)
//                                   )
//                                 }
//                               />
//                             </div>
//                           )}
//                         </div>
//                       ))}
//                   </div>
//                 </div>
//               ))}
//             </div>

//             <div className="flex">
//               <div className="mb-4 mb-6 ml-2">
//                 <h4 className="text-md font-bold mb-2">Primary</h4>
//                 <div className="flex">
//               {Array.from(
//                 new Set(
//                   filterItemsBySearchQuery(nonEventItems).map(
//                     (item) => item.types
//                   )
//                 )
//               ).map((type) => (
//                 <div key={type} className="mb-4 mb-6 ml-2">
//                   <h4 className="text-md font-bold mb-2">{type}</h4>
//                   <div className="grid gap-2">
//                     {filterItemsBySearchQuery(nonEventItems)
//                       .filter((item) => item.types === type)
//                       .sort((a, b) => a.name.localeCompare(b.name))
//                       .map((filteredItem) => (
//                         <div
//                           className={`border p-1 rounded bg-neutral-400 flex items-center`}
//                           key={filteredItem.user_item_id}
//                         >
//                           <div className="flex">
//                             <div className="ml-2">{filteredItem.name}</div>
//                           </div>
//                           <button
//                             className="ml-2 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
//                             onClick={() =>
//                               toggleVisibility(filteredItem.name)
//                             }
//                           >
//                             {itemVisibility[filteredItem.name] ? "Hide" : "Info"}
//                           </button>
//                           {itemVisibility[filteredItem.name] && (
//                             <div className="mt-2">
//                               <div className="flex">
//                                 <div> Map: </div>
//                                 <div className="ml-2">
//                                   {filteredItem.maps_name}
//                                 </div>
//                               </div>
//                               <img
//                                 className="object-scale-down h-48 cursor-pointer"
//                                 src={require(`../imagesitems/${filteredItem.url}`)}
//                                 alt="no image"
//                                 onClick={() =>
//                                   showModal(
//                                     require(`../imagesitems/${filteredItem.url}`)
//                                   )
//                                 }
//                               />
//                             </div>
//                           )}
//                         </div>
//                       ))}
//                   </div>
//                 </div>
//               ))}
//             </div>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Modal */}
//       {modalImageSrc && (
//         <div
//           className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
//           onClick={hideModal}
//         >
//           <div className="relative bg-white p-4 rounded-lg">
//             <button
//               className="absolute top-2 right-2 text-black"
//               onClick={hideModal}
//             >
//               &times;
//             </button>
//             <img
//               className="object-contain max-h-screen"
//               src={modalImageSrc}
//               alt="modal content"
//             />
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default FriendList;
